import React, { useState } from 'react';
import { Check, X, Download, Loader2, Users, RotateCcw, Tv, MessageSquare, Volume2, Pencil, Plus, Play, Square, ChevronDown, ChevronUp, ThumbsDown } from 'lucide-react';
import { ManualAvatarRequestButtons } from './ManualAvatarRequestButtons';
import { AvatarSuggestion } from '../services/workshop';

const formatRole = (role: string) =>
  normalizeRole(role)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Normalize role identifiers — handles LLM-generated variants like "Devil_S_Advocate"
const normalizeRole = (role: string): string => {
  const lower = role.toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
  // Map known mangled variants to canonical roles
  const aliases: Record<string, string> = {
    'devil_s_advocate': 'devils_advocate',
    'devils_advocate': 'devils_advocate',
  };
  return aliases[lower] || lower;
};

interface Avatar {
  role: string;
  name?: string;
  imageUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
}

interface AvatarContributionsTabProps {
  contributions: AvatarSuggestion[];
  avatars: Avatar[];
  onAccept: (contribution: AvatarSuggestion) => void;
  onAcceptWithAudio: (contribution: AvatarSuggestion) => void;
  onIgnore: (index: number) => void;
  onReject: (index: number, reason?: string) => void;
  onStageAudio: (contribution: AvatarSuggestion, index: number) => Promise<void>;
  onRequestAll: () => void;
  onRequestSelected: (avatarRoles: string[]) => void;
  onPushToCinematic?: (contribution: AvatarSuggestion) => void;
  onEditContribution: (index: number, newText: string) => void;
  onCreateContribution: (role: string, text: string) => void;
  onReplayAudio?: (contribution: AvatarSuggestion) => void;
  speakingAvatar?: string | null;
  onStopAudio?: () => void;
  loading?: boolean;
  hasTranscript?: boolean;
  processingAudio?: Set<number>;
  isSecondScreenOpen?: boolean;
  ttsProvider?: 'off' | 'elevenlabs';
}

// ─── Avatar Circle (matches Stitch: size-12, colored bg, emoji/initials) ─────

const AvatarCircle: React.FC<{ name?: string; role: string; imageUrl?: string; primaryColor?: string; secondaryColor?: string; small?: boolean }> = ({
  name,
  role,
  imageUrl,
  primaryColor,
  secondaryColor,
  small = false,
}) => {
  const displayName = name || formatRole(role);
  const initials = displayName
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const sizeClass = small ? 'size-10' : 'size-12';

  if (imageUrl) {
    return (
      <div className="relative">
        <img
          src={imageUrl}
          alt={displayName}
          className={`${sizeClass} rounded-full object-cover border-2 shadow-sm`}
          style={primaryColor ? { borderColor: primaryColor } : { borderColor: 'white' }}
        />
      </div>
    );
  }

  return (
    <div className="relative">
      <div
        className={`${sizeClass} rounded-full flex items-center justify-center text-2xl border-2 shadow-sm`}
        style={{
          backgroundColor: secondaryColor || '#e0e7ff',
          borderColor: primaryColor || '#818cf8',
          color: primaryColor || '#4f46e5',
        }}
      >
        {initials}
      </div>
    </div>
  );
};

// ─── Urgency left bar color (absolute positioned strip) ──────────────────────

const urgencyBarColor = (urgency: 'urgent' | 'important' | 'normal') => {
  switch (urgency) {
    case 'urgent':
      return 'bg-primary';
    case 'important':
      return 'bg-primary/40';
    case 'normal':
      return 'bg-blue-400';
  }
};

// ─── Urgency badge (pill style matching Stitch) ──────────────────────────────

const UrgencyBadge: React.FC<{ urgency: 'urgent' | 'important' | 'normal' }> = ({ urgency }) => {
  const config: Record<string, { bg: string; text: string; border: string; label: string; pulse: boolean }> = {
    urgent: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-100', label: 'Urgent', pulse: true },
    important: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-100', label: 'Medium Priority', pulse: false },
    normal: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-100', label: 'Normal', pulse: false },
  };
  const c = config[urgency];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full ${c.bg} ${c.text} text-xs font-bold border ${c.border}`}>
      {c.pulse ? (
        <span className="size-1.5 rounded-full bg-red-600 animate-pulse" />
      ) : null}
      {c.label}
    </span>
  );
};

// ─── Edit / New Contribution Modal ───────────────────────────────────────────

interface ContributionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (text: string, role?: string) => void;
  avatars: Avatar[];
  initialText?: string;
  initialRole?: string;
  mode: 'edit' | 'create';
}

const ContributionModal: React.FC<ContributionModalProps> = ({
  isOpen,
  onClose,
  onSave,
  avatars,
  initialText = '',
  initialRole,
  mode,
}) => {
  const [text, setText] = useState(initialText);
  const [selectedRole, setSelectedRole] = useState(initialRole || avatars[0]?.role || '');
  const prevOpenRef = React.useRef(false);

  // Reset state only when the modal first opens (false → true transition)
  React.useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      setText(initialText);
      setSelectedRole(initialRole || avatars[0]?.role || '');
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, initialText, initialRole, avatars]);

  if (!isOpen) return null;

  const canSave = text.trim().length > 0 && (mode === 'edit' || selectedRole);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-bold text-navy">
            {mode === 'edit' ? 'Edit Contribution' : 'New Contribution'}
          </h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {mode === 'create' && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Avatar</label>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
              >
                {avatars.map((a) => (
                  <option key={a.role} value={a.role}>
                    {a.name || formatRole(a.role)}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Contribution Text</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={6}
              autoFocus
              className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
              placeholder="Enter the contribution text..."
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => { onSave(text.trim(), mode === 'create' ? selectedRole : undefined); onClose(); }}
            disabled={!canSave}
            className="px-4 py-2 text-sm font-semibold text-navy bg-primary rounded-lg hover:bg-primary-dark hover:text-white transition-colors shadow-sm disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed"
          >
            {mode === 'edit' ? 'Save Changes' : 'Add Contribution'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

export function AvatarContributionsTab({
  contributions,
  avatars,
  onAccept,
  onAcceptWithAudio,
  onIgnore,
  onReject,
  onStageAudio,
  onRequestAll,
  onRequestSelected,
  onPushToCinematic,
  onEditContribution,
  onCreateContribution,
  onReplayAudio,
  speakingAvatar,
  onStopAudio,
  loading = false,
  hasTranscript = false,
  processingAudio = new Set(),
  isSecondScreenOpen = false,
  ttsProvider = 'elevenlabs',
}: AvatarContributionsTabProps) {
  const [selectedAvatarFilter, setSelectedAvatarFilter] = useState<string>('all');
  type SortField = 'timestamp' | 'urgency';
  const [sortField, setSortField] = useState<SortField>('timestamp');
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  const [rejectingIdx, setRejectingIdx] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'edit' | 'create'>('edit');
  const [editingIndex, setEditingIndex] = useState<number>(-1);
  const [editingText, setEditingText] = useState('');
  const [editingRole, setEditingRole] = useState<string | undefined>();

  const openEditModal = (contribution: AvatarSuggestion) => {
    const idx = getOriginalIndex(contribution);
    setEditingIndex(idx);
    setEditingText(contribution.response);
    setEditingRole(contribution.role);
    setModalMode('edit');
    setModalOpen(true);
  };

  const openCreateModal = () => {
    setEditingIndex(-1);
    setEditingText('');
    setEditingRole(undefined);
    setModalMode('create');
    setModalOpen(true);
  };

  const handleModalSave = (text: string, role?: string) => {
    if (modalMode === 'edit' && editingIndex >= 0) {
      onEditContribution(editingIndex, text);
    } else if (modalMode === 'create' && role) {
      onCreateContribution(role, text);
    }
  };

  const uniqueAvatarRoles = Array.from(new Set(contributions.map((c) => normalizeRole(c.role))));

  // Filter
  let filtered =
    selectedAvatarFilter === 'all'
      ? contributions
      : contributions.filter((c) => normalizeRole(c.role) === selectedAvatarFilter);

  // Sort
  filtered = [...filtered].sort((a, b) => {
    const aT = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bT = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    if (sortField === 'urgency') {
      const order = { urgent: 0, important: 1, normal: 2 };
      const urgDiff = order[a.urgency] - order[b.urgency];
      if (urgDiff !== 0) return urgDiff;
    }
    return bT - aT;
  });

  // Categorise
  const pending = filtered.filter((c) => !c.state || c.state === 'pending');
  const accepted = filtered.filter((c) => c.state === 'accepted');
  const ignored = filtered.filter((c) => c.state === 'ignored' || c.state === 'rejected');

  // Counts (unfiltered)
  const pendingCount = contributions.filter((c) => !c.state || c.state === 'pending').length;
  const acceptedCount = contributions.filter((c) => c.state === 'accepted').length;
  const ignoredCount = contributions.filter((c) => c.state === 'ignored' || c.state === 'rejected').length;

  // Helpers
  const getOriginalIndex = (contribution: AvatarSuggestion) =>
    contributions.findIndex((c) => c === contribution);

  const handleAccept = (contribution: AvatarSuggestion) => onAccept(contribution);
  const handleAcceptWithAudio = (contribution: AvatarSuggestion) => onAcceptWithAudio(contribution);
  const handleIgnore = (contribution: AvatarSuggestion) => {
    const idx = getOriginalIndex(contribution);
    if (idx !== -1) onIgnore(idx);
  };

  const handleReject = (contribution: AvatarSuggestion, reason?: string) => {
    const idx = getOriginalIndex(contribution);
    if (idx !== -1) {
      onReject(idx, reason);
      setRejectingIdx(null);
      setRejectReason('');
    }
  };
  const handleStageAudio = async (contribution: AvatarSuggestion) => {
    const idx = getOriginalIndex(contribution);
    if (idx !== -1) await onStageAudio(contribution, idx);
  };

  const formatTimestamp = (createdAt?: string) => {
    if (!createdAt) return '';
    const date = new Date(createdAt);
    const diff = Date.now() - date.getTime();
    const mins = Math.floor(diff / 60000);
    const time = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    const dateStr = date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
    if (mins < 1) return `Just now · ${time}`;
    if (mins < 60) return `${mins}m ago · ${time}`;
    return `${dateStr} · ${time}`;
  };

  const findAvatar = (role: string) => avatars.find((a) => normalizeRole(a.role) === normalizeRole(role));

  const toggleCard = (idx: number) => {
    setExpandedCards((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="font-display flex flex-col h-full">
      {/* ── Summary & Action Header (white bar) ──────────────────── */}
      <div className="px-8 py-6 bg-white border-b border-slate-200 shadow-sm shrink-0">
        <div className="flex flex-col gap-6">
          {/* Top Row: Title & Primary Action */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-navy tracking-tight">Manage Contributions</h2>
              <p className="text-slate-500 text-sm mt-1">Review and stage avatar suggestions for the live session.</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={openCreateModal}
                className="flex items-center gap-2 bg-white border border-primary text-primary font-semibold text-sm py-2.5 px-4 rounded-lg hover:bg-primary hover:text-navy transition-colors shadow-sm"
              >
                <Plus size={16} />
                New Contribution
              </button>
              <ManualAvatarRequestButtons
                avatars={avatars}
                onRequestAll={onRequestAll}
                onRequestSelected={onRequestSelected}
                loading={loading}
                disabled={loading || !hasTranscript}
              />
            </div>
          </div>

          {/* Bottom Row: Stats & Filters */}
          <div className="flex flex-wrap items-center justify-between gap-4 mt-2">
            {/* Status Counter Cards */}
            <div className="flex items-center gap-4">
              {/* Pending - special teal accent */}
              <div className="flex flex-col bg-white border border-primary/30 rounded-lg px-4 py-2 min-w-[120px] shadow-sm relative overflow-hidden group">
                <div className="absolute inset-0 bg-primary/5 group-hover:bg-primary/10 transition-colors" />
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider z-10">Pending</span>
                <div className="flex items-center gap-2 z-10">
                  <span className="text-2xl font-bold text-navy">{pendingCount}</span>
                  {pendingCount > 0 && <span className="size-2 rounded-full bg-primary animate-pulse" />}
                </div>
              </div>
              {/* Accepted */}
              <div className="flex flex-col bg-white border border-slate-200 rounded-lg px-4 py-2 min-w-[120px] shadow-sm">
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Accepted</span>
                <span className="text-2xl font-bold text-emerald-600">{acceptedCount}</span>
              </div>
              {/* Ignored */}
              <div className="flex flex-col bg-white border border-slate-200 rounded-lg px-4 py-2 min-w-[120px] shadow-sm opacity-60">
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Ignored</span>
                <span className="text-2xl font-bold text-slate-600">{ignoredCount}</span>
              </div>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3">
              <div className="h-8 w-[1px] bg-slate-200 mx-2" />
              <select
                value={selectedAvatarFilter}
                onChange={(e) => setSelectedAvatarFilter(e.target.value)}
                className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:border-primary/50 hover:text-navy transition-colors cursor-pointer appearance-none pr-8"
              >
                <option value="all">Filter by Avatar</option>
                {uniqueAvatarRoles.map((role) => (
                  <option key={role} value={role}>{formatRole(role)}</option>
                ))}
              </select>
              <select
                value={sortField}
                onChange={(e) => setSortField(e.target.value as SortField)}
                className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:border-primary/50 hover:text-navy transition-colors cursor-pointer appearance-none pr-8"
              >
                <option value="urgency">Sort: Urgency</option>
                <option value="timestamp">Sort: Newest</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* ── Scrollable Content List ──────────────────────────────── */}
      <div className="flex-1 overflow-y-auto p-8 space-y-4">
        {speakingAvatar && onStopAudio && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Volume2 size={20} className="text-red-500 animate-pulse" />
              <span className="text-sm font-semibold text-red-700">
                {formatRole(speakingAvatar)} is speaking...
              </span>
            </div>
            <button
              onClick={onStopAudio}
              className="flex items-center gap-2 bg-red-500 text-white font-semibold text-sm py-2 px-4 rounded-lg hover:bg-red-600 transition-colors"
            >
              <Square size={14} />
              Stop
            </button>
          </div>
        )}
        {contributions.length === 0 ? (
          <div className="py-16 text-center bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl">
            <Users className="mx-auto text-slate-300 mb-4" size={48} />
            <h3 className="text-base font-semibold text-slate-500 mb-1">No Contributions Yet</h3>
            <p className="text-sm text-slate-400">
              {hasTranscript
                ? 'Use the button above to request avatar responses'
                : 'Start a conversation to receive avatar contributions'}
            </p>
          </div>
        ) : (
          <>
            {/* ── Pending Cards ─────────────────────────────────── */}
            {pending.map((contribution) => {
              const idx = getOriginalIndex(contribution);
              const avatar = findAvatar(contribution.role);
              const hasAudio = !!(contribution.audioUrl || contribution.audioBase64);
              const isProcessing = processingAudio.has(idx);
              const isPending = !contribution.state || contribution.state === 'pending';
              const isExpanded = expandedCards.has(idx);

              return (
                <article
                  key={`pending-${idx}`}
                  className="bg-white rounded-xl shadow-sm border border-slate-200 hover:shadow-md hover:border-primary/40 transition-all group relative overflow-hidden"
                >
                  {/* Avatar color left bar (fallback to urgency color) */}
                  <div
                    className={`absolute left-0 top-0 bottom-0 w-1.5 ${avatar?.primaryColor ? '' : urgencyBarColor(contribution.urgency)}`}
                    style={avatar?.primaryColor ? { backgroundColor: avatar.primaryColor } : undefined}
                  />

                  {/* Collapsed layout */}
                  <div className="flex items-center gap-4 p-4 pl-5 cursor-pointer" onClick={() => toggleCard(idx)}>
                    {/* Avatar (small in collapsed) */}
                    <div className="shrink-0">
                      <AvatarCircle
                        name={contribution.name || avatar?.name}
                        role={contribution.role}
                        imageUrl={avatar?.imageUrl}
                        primaryColor={avatar?.primaryColor}
                        secondaryColor={avatar?.secondaryColor}
                        small
                      />
                    </div>

                    {/* Name + Content preview */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-navy text-sm truncate">
                          {contribution.name || avatar?.name || formatRole(contribution.role)}
                        </h3>
                        <UrgencyBadge urgency={contribution.urgency} />
                        <span className="text-xs text-slate-400 font-medium ml-auto shrink-0">{formatTimestamp(contribution.createdAt)}</span>
                      </div>
                      <p className={`text-slate-800 text-sm leading-snug ${isExpanded ? '' : 'line-clamp-2'}`}>
                        {contribution.response}
                      </p>
                      {isExpanded && contribution.governorReason && (
                        <p className="text-xs text-slate-400 italic mt-1">
                          {contribution.governorReason}
                        </p>
                      )}
                    </div>

                    {/* Expand/Collapse chevron */}
                    <button className="shrink-0 p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
                      {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                  </div>

                  {/* Action buttons (collapsed: underneath) */}
                  {!isExpanded && (
                    <div className="px-5 pb-3 pt-0 flex flex-wrap gap-2 justify-end" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleAccept(contribution)}
                        disabled={!isPending || isProcessing}
                        className="flex items-center justify-center gap-1.5 bg-primary text-navy font-semibold text-xs py-1.5 px-3 rounded-lg hover:bg-primary-dark hover:text-white transition-colors shadow-sm disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed"
                      >
                        <MessageSquare size={14} />
                        Accept
                      </button>
                      <button
                        onClick={() => handleAcceptWithAudio(contribution)}
                        disabled={!isPending || isProcessing || ttsProvider === 'off'}
                        className="flex items-center justify-center gap-1.5 bg-white border border-primary text-primary font-semibold text-xs py-1.5 px-3 rounded-lg hover:bg-primary hover:text-navy transition-colors shadow-sm disabled:bg-slate-200 disabled:text-slate-400 disabled:border-slate-200 disabled:cursor-not-allowed"
                      >
                        <Volume2 size={14} />
                        Accept & Speak
                      </button>
                      <button
                        onClick={() => handleIgnore(contribution)}
                        disabled={!isPending || isProcessing}
                        className="flex items-center justify-center gap-1.5 bg-white border border-slate-300 text-slate-500 font-medium text-xs py-1.5 px-3 rounded-lg hover:bg-slate-50 hover:text-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <X size={14} />
                        Dismiss
                      </button>
                      <button
                        onClick={() => handleStageAudio(contribution)}
                        disabled={isProcessing || hasAudio || ttsProvider === 'off'}
                        className={`flex items-center justify-center gap-1.5 font-medium text-xs py-1.5 px-3 rounded-lg transition-colors ${
                          hasAudio
                            ? 'bg-emerald-50 text-emerald-600 border border-emerald-200 cursor-default'
                            : ttsProvider === 'off' || isProcessing
                            ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                            : 'bg-white border border-slate-300 text-slate-600 hover:bg-slate-50 hover:text-slate-800'
                        }`}
                      >
                        {isProcessing ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : hasAudio ? (
                          <Check size={14} />
                        ) : (
                          <Download size={14} />
                        )}
                        {isProcessing ? 'Staging...' : hasAudio ? 'Staged' : 'Stage Audio'}
                      </button>
                    </div>
                  )}

                  {/* Expanded content */}
                  {isExpanded && (
                    <div className="px-5 pb-5 pt-0 border-t border-slate-100">
                      <div className="flex flex-wrap gap-2 mt-3 justify-end">
                        <button
                          onClick={() => openEditModal(contribution)}
                          className="flex items-center justify-center gap-2 bg-white border border-slate-300 text-slate-600 font-medium text-sm py-2 px-4 rounded-lg hover:bg-slate-50 hover:text-navy hover:border-primary/50 transition-colors"
                        >
                          <Pencil size={16} />
                          Edit
                        </button>
                        <button
                          onClick={() => handleAccept(contribution)}
                          disabled={!isPending || isProcessing}
                          className="flex items-center justify-center gap-2 bg-primary text-navy font-semibold text-sm py-2 px-4 rounded-lg hover:bg-primary-dark hover:text-white transition-colors shadow-sm disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed"
                        >
                          <MessageSquare size={16} />
                          Accept
                        </button>
                        <button
                          onClick={() => handleAcceptWithAudio(contribution)}
                          disabled={!isPending || isProcessing || ttsProvider === 'off'}
                          className="flex items-center justify-center gap-2 bg-white border border-primary text-primary font-semibold text-sm py-2 px-4 rounded-lg hover:bg-primary hover:text-navy transition-colors shadow-sm disabled:bg-slate-200 disabled:text-slate-400 disabled:border-slate-200 disabled:cursor-not-allowed"
                        >
                          <Volume2 size={16} />
                          Accept & Speak
                        </button>
                        <button
                          onClick={() => handleIgnore(contribution)}
                          disabled={!isPending || isProcessing}
                          className="flex items-center justify-center gap-2 bg-white border border-slate-300 text-slate-600 font-medium text-sm py-2 px-4 rounded-lg hover:bg-slate-50 hover:text-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <X size={16} />
                          Dismiss
                        </button>
                        {isPending && rejectingIdx !== idx && (
                          <button
                            onClick={() => { setRejectingIdx(idx); setRejectReason(''); }}
                            disabled={isProcessing}
                            className="flex items-center justify-center gap-2 bg-white border border-red-200 text-red-500 font-medium text-sm py-2 px-4 rounded-lg hover:bg-red-50 hover:border-red-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ThumbsDown size={16} />
                            Reject...
                          </button>
                        )}
                        {rejectingIdx === idx && (
                          <div className="w-full flex flex-col gap-2 mt-1 p-3 bg-red-50 border border-red-100 rounded-lg">
                            <p className="text-xs font-semibold text-red-600">Reject this contribution?</p>
                            <input
                              type="text"
                              value={rejectReason}
                              onChange={(e) => setRejectReason(e.target.value)}
                              placeholder="Reason (optional)"
                              maxLength={200}
                              className="text-xs border border-red-200 rounded-md px-2 py-1.5 w-full focus:outline-none focus:ring-1 focus:ring-red-300 bg-white"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleReject(contribution, rejectReason || undefined);
                                if (e.key === 'Escape') { setRejectingIdx(null); setRejectReason(''); }
                              }}
                              autoFocus
                            />
                            <div className="flex gap-2 justify-end">
                              <button
                                onClick={() => { setRejectingIdx(null); setRejectReason(''); }}
                                className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1 rounded"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => handleReject(contribution, rejectReason || undefined)}
                                className="text-xs font-semibold text-white bg-red-500 hover:bg-red-600 px-3 py-1 rounded-md transition-colors"
                              >
                                Reject
                              </button>
                            </div>
                          </div>
                        )}
                        {/* Stage Audio */}
                        {isPending && (
                          <button
                            onClick={() => handleStageAudio(contribution)}
                            disabled={isProcessing || hasAudio || ttsProvider === 'off'}
                            className={`flex items-center justify-center gap-2 font-medium text-sm py-2 px-4 rounded-lg transition-colors ${
                              hasAudio
                                ? 'bg-emerald-50 text-emerald-600 border border-emerald-200 cursor-default'
                                : ttsProvider === 'off' || isProcessing
                                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                : 'bg-white border border-slate-300 text-slate-600 hover:bg-slate-50 hover:text-slate-800'
                            }`}
                          >
                            {isProcessing ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : hasAudio ? (
                              <Check size={16} />
                            ) : (
                              <Download size={16} />
                            )}
                            {isProcessing ? 'Staging...' : hasAudio ? 'Staged' : 'Stage Audio'}
                          </button>
                        )}
                        {/* Push to Cinematic Screen */}
                        {isSecondScreenOpen && onPushToCinematic && (
                          <button
                            onClick={() => onPushToCinematic(contribution)}
                            className="flex items-center justify-center gap-2 bg-white border border-purple-200 text-purple-600 font-medium text-sm py-2 px-4 rounded-lg hover:bg-purple-50 hover:border-purple-300 transition-colors"
                          >
                            <Tv size={16} />
                            Push to Cinematic
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </article>
              );
            })}

            {/* ── Section Divider: Accepted History ──────────────── */}
            {accepted.length > 0 && (
              <>
                <div className="flex items-center gap-4 py-4">
                  <div className="h-[1px] bg-slate-200 flex-1" />
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Accepted History</span>
                  <div className="h-[1px] bg-slate-200 flex-1" />
                </div>
                {accepted.map((contribution) => {
                  const idx = getOriginalIndex(contribution);
                  const avatar = findAvatar(contribution.role);
                  const isExpanded = expandedCards.has(idx);

                  return (
                    <article
                      key={`accepted-${idx}`}
                      className="bg-slate-50/50 rounded-xl shadow-sm border border-emerald-200/50 relative overflow-hidden opacity-90"
                    >
                      <div
                        className={`absolute left-0 top-0 bottom-0 w-1.5 ${avatar?.primaryColor ? '' : 'bg-emerald-500'}`}
                        style={avatar?.primaryColor ? { backgroundColor: avatar.primaryColor } : undefined}
                      />

                      {/* Header row */}
                      <div className="flex items-center gap-4 p-4 pl-5 cursor-pointer" onClick={() => toggleCard(idx)}>
                        <div className="shrink-0 opacity-75">
                          <AvatarCircle
                            name={contribution.name || avatar?.name}
                            role={contribution.role}
                            imageUrl={avatar?.imageUrl}
                            primaryColor={avatar?.primaryColor}
                            secondaryColor={avatar?.secondaryColor}
                            small
                          />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-bold text-navy text-sm truncate">
                              {contribution.name || avatar?.name || formatRole(contribution.role)}
                            </h3>
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">
                              <Check size={14} />
                              Accepted
                            </span>
                            <span className="text-xs text-slate-400 font-medium ml-auto shrink-0">{formatTimestamp(contribution.createdAt)}</span>
                          </div>
                          <p className={`text-slate-600 text-sm leading-snug ${isExpanded ? '' : 'line-clamp-2'}`}>
                            {contribution.response}
                          </p>
                        </div>

                        <button className="shrink-0 p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
                          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>
                      </div>

                      {/* Collapsed buttons */}
                      {!isExpanded && contribution.audioS3Key && onReplayAudio && (
                        <div className="px-5 pb-3 pt-0 flex flex-wrap gap-2 justify-end">
                          {speakingAvatar === contribution.role && onStopAudio ? (
                            <button
                              onClick={onStopAudio}
                              className="flex items-center justify-center gap-1.5 bg-red-500 text-white font-medium text-xs py-1.5 px-3 rounded-lg hover:bg-red-600 transition-colors"
                            >
                              <Square size={14} />
                              Stop
                            </button>
                          ) : (
                            <button
                              onClick={() => onReplayAudio(contribution)}
                              className="flex items-center justify-center gap-1.5 bg-white border border-emerald-200 text-emerald-600 font-medium text-xs py-1.5 px-3 rounded-lg hover:bg-emerald-50 hover:border-emerald-300 transition-colors"
                            >
                              <Play size={14} />
                              Replay
                            </button>
                          )}
                        </div>
                      )}

                      {/* Expanded content */}
                      {isExpanded && (
                        <div className="px-5 pb-4 pt-0 border-t border-emerald-100">
                          {contribution.audioS3Key && onReplayAudio && (
                            <div className="flex flex-wrap gap-2 mt-3 justify-end">
                              {speakingAvatar === contribution.role && onStopAudio ? (
                                <button
                                  onClick={onStopAudio}
                                  className="flex items-center justify-center gap-2 bg-red-500 text-white font-medium text-sm py-2 px-4 rounded-lg hover:bg-red-600 transition-colors"
                                >
                                  <Square size={16} />
                                  Stop
                                </button>
                              ) : (
                                <button
                                  onClick={() => onReplayAudio(contribution)}
                                  className="flex items-center justify-center gap-2 bg-white border border-emerald-200 text-emerald-600 font-medium text-sm py-2 px-4 rounded-lg hover:bg-emerald-50 hover:border-emerald-300 transition-colors"
                                >
                                  <Play size={16} />
                                  Replay
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </article>
                  );
                })}
              </>
            )}

            {/* ── Section Divider: Ignored ───────────────────────── */}
            {ignored.length > 0 && (
              <>
                <div className="flex items-center gap-4 py-4">
                  <div className="h-[1px] bg-slate-200 flex-1" />
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Ignored</span>
                  <div className="h-[1px] bg-slate-200 flex-1" />
                </div>
                {ignored.map((contribution) => {
                  const idx = getOriginalIndex(contribution);
                  const avatar = findAvatar(contribution.role);
                  const isExpanded = expandedCards.has(idx);

                  return (
                    <article
                      key={`ignored-${idx}`}
                      className="bg-slate-100 rounded-xl shadow-sm border border-slate-200 relative overflow-hidden opacity-60 hover:opacity-100 transition-opacity"
                    >
                      <div
                        className={`absolute left-0 top-0 bottom-0 w-1.5 ${avatar?.primaryColor ? 'opacity-40' : 'bg-slate-400'}`}
                        style={avatar?.primaryColor ? { backgroundColor: avatar.primaryColor } : undefined}
                      />

                      {/* Header row */}
                      <div className="flex items-center gap-4 p-4 pl-5 cursor-pointer" onClick={() => toggleCard(idx)}>
                        <div className="shrink-0 grayscale">
                          <AvatarCircle
                            name={contribution.name || avatar?.name}
                            role={contribution.role}
                            imageUrl={avatar?.imageUrl}
                            primaryColor={avatar?.primaryColor}
                            secondaryColor={avatar?.secondaryColor}
                            small
                          />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-bold text-navy text-sm truncate">
                              {contribution.name || avatar?.name || formatRole(contribution.role)}
                            </h3>
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-slate-200 text-slate-600 text-xs font-bold">
                              <X size={14} />
                              Ignored
                            </span>
                            <span className="text-xs text-slate-400 font-medium ml-auto shrink-0">{formatTimestamp(contribution.createdAt)}</span>
                          </div>
                          <p className={`text-slate-500 text-sm leading-snug line-through decoration-slate-400 ${isExpanded ? '' : 'line-clamp-2'}`}>
                            {contribution.response}
                          </p>
                        </div>

                        <button className="shrink-0 p-1 rounded-lg hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors">
                          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>
                      </div>

                      {/* Collapsed buttons */}
                      {!isExpanded && (
                        <div className="px-5 pb-3 pt-0 flex flex-wrap gap-2 justify-end">
                          <button
                            onClick={() => handleAccept(contribution)}
                            className="flex items-center justify-center gap-1.5 bg-white border border-slate-300 text-slate-600 font-medium text-xs py-1.5 px-3 rounded-lg hover:bg-slate-50 hover:text-slate-800 transition-colors"
                          >
                            <RotateCcw size={14} />
                            Restore
                          </button>
                        </div>
                      )}

                      {/* Expanded content */}
                      {isExpanded && (
                        <div className="px-5 pb-4 pt-0 border-t border-slate-200">
                          <div className="flex flex-wrap gap-2 mt-3 justify-end">
                            <button
                              onClick={() => handleAccept(contribution)}
                              className="flex items-center justify-center gap-2 bg-white border border-slate-300 text-slate-600 font-medium text-sm py-2 px-4 rounded-lg hover:bg-slate-50 hover:text-slate-800 transition-colors"
                            >
                              <RotateCcw size={16} />
                              Restore
                            </button>
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })}
              </>
            )}

            {/* Empty pending message */}
            {pending.length === 0 && (accepted.length > 0 || ignored.length > 0) && (
              <p className="text-sm text-slate-400 text-center py-4">No pending contributions to review</p>
            )}
          </>
        )}
      </div>

      {/* Edit / New Contribution Modal */}
      <ContributionModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleModalSave}
        avatars={avatars}
        initialText={editingText}
        initialRole={editingRole}
        mode={modalMode}
      />
    </div>
  );
}
